import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ModalContainer } from '../../../../../components/modals/ModalContainer'
import { ModalHeader } from '../../../../../components/modals/ModalHeader'
import { ModalFooter } from '../../../../../components/modals/ModalFooter'
import { requestDefaulAuth } from '../../../../../http/httpRequest'
import { listToString } from '../../../../../utils/dataValidations'

const ESTADOS = [
    { key: 'superNuevo', label: 'Súper Nuevo' },
    { key: 'nuevo', label: 'Nuevo' },
    { key: 'semiNuevo', label: 'Semi-Nuevo' },
    { key: 'usado', label: 'Usado' },
    { key: 'marcado', label: 'Marcado' },
]

const ESTADO_DEFAULT = { superNuevo: 0, nuevo: 0, semiNuevo: 0, usado: 0, marcado: 0, obs: '' }

const sumaEstados = (row) => ESTADOS.reduce((acc, e) => acc + (Number(row?.[e.key]) || 0), 0)

// Paso intermedio entre el formulario de búsqueda y el PDF: pide al backend qué productos tuvieron
// movimiento ese día (dinámico, no hardcodeado) y deja repartir manualmente, por producto retornable,
// la cantidad total entre los 5 estados físicos del papel real de la empresa. Este reparto vive SOLO
// en este estado de React — nunca se envía al backend, solo alimenta el PDF vía onGenerate.
export const AlmacenNotaAjusteEstados = ({
    searchValues,
    urlApi,
    receptorLabel = 'receptor',
    setModal,
    onGenerate,
}) => {
    const dispatch = useDispatch()
    const [notas, setNotas] = useState(null)
    const [estados, setEstados] = useState({})

    useEffect(() => {
        requestDefaulAuth('post', urlApi, searchValues, setNotas, dispatch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const rowKey = (notaIndex, producto_id) => `${notaIndex}-${producto_id}`

    const getRow = (key) => estados[key] ?? ESTADO_DEFAULT

    const setRowField = (key, field, value) => {
        setEstados((prev) => ({
            ...prev,
            [key]: { ...getRow(key), [field]: value },
        }))
    }

    const allValid = notas != null && notas.every((nota, notaIndex) =>
        (nota.productos ?? []).every((p) => {
            if (!p.retornable) return true
            return sumaEstados(getRow(rowKey(notaIndex, p.producto_id))) === p.total
        })
    )

    const handleGenerate = () => {
        if (!allValid) return
        const assembled = notas.map((nota, notaIndex) => ({
            ...nota,
            productos: (nota.productos ?? []).map((p) => {
                const row = getRow(rowKey(notaIndex, p.producto_id))
                return {
                    ...p,
                    estados: p.retornable ? {
                        superNuevo: Number(row.superNuevo) || 0,
                        nuevo: Number(row.nuevo) || 0,
                        semiNuevo: Number(row.semiNuevo) || 0,
                        usado: Number(row.usado) || 0,
                        marcado: Number(row.marcado) || 0,
                    } : null,
                    obs: row.obs || '',
                }
            }),
        }))
        onGenerate(assembled)
    }

    return (
        <ModalContainer setModal={setModal} width='md:max-w-6xl'>
            <form
                className='relative bg-white rounded-lg shadow dark:bg-gray-700'
                onSubmit={(e) => { e.preventDefault(); handleGenerate() }}
            >
                <ModalHeader setModal={setModal} label='Ajustar estado de envases' />
                <div className='p-3 space-y-6 max-h-[65vh] overflow-auto'>
                    {notas === null ? (
                        <div className='flex justify-center py-16'>
                            <svg aria-hidden='true' className='inline w-12 h-12 text-gray-200 animate-spin dark:text-gray-500 dark:fill-yellow-500 fill-yellow-600' viewBox='0 0 100 101' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <path d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z' fill='currentColor' />
                                <path d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z' fill='currentFill' />
                            </svg>
                        </div>
                    ) : notas.length === 0 ? (
                        <div className='text-center text-sm text-gray-500 dark:text-gray-400 italic py-8'>
                            No se encontraron receptores.
                        </div>
                    ) : (
                        notas.map((nota, notaIndex) => {
                            const receptorNombre = listToString([
                                nota?.[receptorLabel]?.encargado_nombres,
                                nota?.[receptorLabel]?.encargado_apellido_paterno,
                                nota?.[receptorLabel]?.encargado_apellido_materno,
                            ], ' ')
                            const productos = nota.productos ?? []
                            return (
                                <div key={notaIndex} className='border border-gray-300 dark:border-gray-500 rounded-lg overflow-hidden'>
                                    <div className='bg-gray-100 dark:bg-gray-800 px-3 py-2 font-semibold text-gray-800 dark:text-gray-100'>
                                        {receptorNombre || `Receptor #${notaIndex + 1}`} — N° {nota.boleta}
                                    </div>
                                    {productos.length === 0 ? (
                                        <div className='p-3 text-sm text-gray-500 dark:text-gray-400 italic'>
                                            Sin movimientos ese día.
                                        </div>
                                    ) : (
                                        <div className='overflow-x-auto'>
                                            <table className='w-full text-sm text-left'>
                                                <thead className='bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300'>
                                                    <tr>
                                                        <th className='px-2 py-1'>Producto</th>
                                                        <th className='px-2 py-1 text-center'>Saldo anterior</th>
                                                        <th className='px-2 py-1 text-center'>Súper Nuevo</th>
                                                        <th className='px-2 py-1 text-center'>Nuevo</th>
                                                        <th className='px-2 py-1 text-center'>Semi-Nuevo</th>
                                                        <th className='px-2 py-1 text-center'>Usado</th>
                                                        <th className='px-2 py-1 text-center'>Marcado</th>
                                                        <th className='px-2 py-1 text-center'>Total</th>
                                                        <th className='px-2 py-1'>Observaciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {productos.map((p) => {
                                                        const key = rowKey(notaIndex, p.producto_id)
                                                        const row = getRow(key)
                                                        const suma = sumaEstados(row)
                                                        const descuadrado = p.retornable && suma !== p.total
                                                        return (
                                                            <tr key={p.producto_id} className='border-t border-gray-200 dark:border-gray-600'>
                                                                <td className='px-2 py-1 font-medium'>
                                                                    {p.nombre}{p.capacidad ? ` (${p.capacidad})` : ''}
                                                                </td>
                                                                <td className='px-2 py-1 text-center'>{p.saldoAnterior}</td>
                                                                {p.retornable ? (
                                                                    ESTADOS.map((e) => (
                                                                        <td key={e.key} className='px-1 py-1'>
                                                                            <input
                                                                                type='number'
                                                                                min='0'
                                                                                className={`w-16 text-center rounded border ${descuadrado ? 'border-red-500' : 'border-gray-300 dark:border-gray-500'} dark:bg-gray-800 dark:text-white`}
                                                                                value={row[e.key]}
                                                                                onChange={(ev) => setRowField(key, e.key, ev.target.value)}
                                                                            />
                                                                        </td>
                                                                    ))
                                                                ) : (
                                                                    <td className='px-2 py-1 text-center text-gray-400' colSpan={5}>—</td>
                                                                )}
                                                                <td className={`px-2 py-1 text-center font-semibold ${descuadrado ? 'text-red-600' : ''}`}>
                                                                    {p.retornable ? `${suma}/${p.total}` : p.total}
                                                                </td>
                                                                <td className='px-2 py-1'>
                                                                    <input
                                                                        type='text'
                                                                        className='w-full rounded border border-gray-300 dark:border-gray-500 dark:bg-gray-800 dark:text-white'
                                                                        value={row.obs}
                                                                        onChange={(ev) => setRowField(key, 'obs', ev.target.value)}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )
                        })
                    )}
                </div>
                <ModalFooter
                    buttonAccept='Generar nota'
                    cancel={setModal}
                    disabled={!allValid}
                />
            </form>
        </ModalContainer>
    )
}

export default AlmacenNotaAjusteEstados
